import * as THREE from 'three';
import { selectRenderProfile } from './RenderQuality.js';
import type { RenderProfile } from './RenderQuality.js';

export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly camera: THREE.PerspectiveCamera;
  readonly profile: RenderProfile;
  private container: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
    this.profile = selectRenderProfile();

    // Camera
    this.camera = new THREE.PerspectiveCamera(
      72,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 1.5, 0); // Seated eye height

    // Cinematic is the default presentation. A stable retro profile remains
    // available for low-spec devices and visual comparisons via ?render=retro.
    this.renderer = new THREE.WebGLRenderer({
      antialias: this.profile.antialias,
      alpha: false,
      powerPreference: 'high-performance',
    });
    const pixelRatio = Math.min(window.devicePixelRatio || 1, this.profile.maxPixelRatio)
      * this.profile.resolutionScale;
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = this.profile.softShadows
      ? THREE.PCFSoftShadowMap
      : THREE.BasicShadowMap;
    this.renderer.toneMapping = this.profile.filmicToneMapping
      ? THREE.ACESFilmicToneMapping
      : THREE.NoToneMapping;
    this.renderer.toneMappingExposure = this.profile.exposure;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.domElement.style.imageRendering = this.profile.id === 'retro' ? 'pixelated' : 'auto';

    container.appendChild(this.renderer.domElement);

    // Handle resize
    window.addEventListener('resize', this.onResize);
  }

  async initXR(): Promise<boolean> {
    if (!navigator.xr) return false;

    const supported = await navigator.xr.isSessionSupported('immersive-vr');
    if (supported) {
      this.renderer.xr.enabled = true;
      this.renderer.xr.setFoveation(1.0); // Max foveation for performance
      // 'local': origin at the head's pose when the session starts, i.e. the
      // player is seated exactly where the cockpit rig puts them.
      this.renderer.xr.setReferenceSpaceType('local');
      this.renderer.xr.setFramebufferScaleFactor(this.profile.xrFramebufferScale);
    }
    return supported;
  }

  async enterVR(): Promise<XRSession | null> {
    if (!navigator.xr) return null;

    try {
      const session = await navigator.xr.requestSession('immersive-vr', {
        optionalFeatures: ['hand-tracking'],
      });
      // Dynamic shadow maps are the largest avoidable GPU cost in the mobile
      // stereo path. The baked/neon materials and ambient lighting retain the
      // art direction without making head motion miss its frame deadline.
      this.renderer.shadowMap.enabled = false;
      await this.renderer.xr.setSession(session);

      // Prefer the headset's lowest advertised refresh rate. A locked 72 Hz
      // is markedly more comfortable than repeatedly missing a 90/120 Hz
      // target and falling into runtime reprojection.
      const xrSession = session as XRSession & {
        supportedFrameRates?: Float32Array;
        updateTargetFrameRate?: (rate: number) => Promise<void>;
      };
      const rates = xrSession.supportedFrameRates ? Array.from(xrSession.supportedFrameRates) : [];
      if (xrSession.updateTargetFrameRate && rates.length > 0) {
        const target = Math.min(...rates);
        void xrSession.updateTargetFrameRate(target).catch(() => undefined);
      }
      session.addEventListener('end', () => {
        this.renderer.shadowMap.enabled = true;
      }, { once: true });
      return session;
    } catch (e) {
      this.renderer.shadowMap.enabled = true;
      console.error('Failed to enter VR:', e);
      return null;
    }
  }

  render(scene: THREE.Scene): void {
    this.renderer.render(scene, this.camera);
  }

  private onResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    this.renderer.dispose();
    this.container.removeChild(this.renderer.domElement);
  }
}
